import { ChakraFlex, FilterElement } from '@equip.health/ui';

import {
  DateRangeFilter,
  MinMaxFilter,
  ProviderFilter,
  SelectFilter,
  StatusFilter,
} from './Filters';
import {
  InquiryASFilterKeys,
  InquiryFilterConfig,
  InquiryFilterTypes,
  InquiryFilterValue,
  InquiryPSRFilterKeys,
} from '~/lib/constants/inquiry.constants';
import { getDefaultInquiryDateMin } from '~/lib/util/inquiry.util';

interface FilterBuilderProps {
  id: string;
  filters: InquiryFilterConfig[];
  onChange: (
    key: InquiryPSRFilterKeys | InquiryASFilterKeys,
    value: InquiryFilterValue,
  ) => void;
}

const FilterWrapper = ({ id, children }) => {
  return (
    <div data-testid={`${id}-filter`} key={id}>
      {children}
    </div>
  );
};

const FilterBuilder = ({ id, filters, onChange }: FilterBuilderProps) => {
  const handleInputChange = (
    key: InquiryPSRFilterKeys | InquiryASFilterKeys,
    newValue: InquiryFilterValue,
  ) => {
    onChange(key, newValue);
  };

  return (
    <ChakraFlex
      flexDirection="column"
      gap="12px"
      paddingLeft="20px"
      paddingRight="20px"
      width="100%"
    >
      {filters.map(
        ({
          defaultValue,
          isSingleSelect,
          key,
          label,
          minMax,
          options,
          placeholder,
          providerFilterData,
          type,
        }) => {
          const { max, min } = minMax ?? {};

          return (
            <FilterWrapper id={key} key={`${id}__${key}`}>
              {(() => {
                switch (type) {
                  case InquiryFilterTypes.STATUS:
                    return (
                      <StatusFilter
                        id={`${id}__${key}`}
                        label={label}
                        onStatusChange={(status) =>
                          handleInputChange(key, status)
                        }
                        status={defaultValue as FilterElement[]}
                      />
                    );
                  case InquiryFilterTypes.PROVIDER:
                    return (
                      <ProviderFilter
                        ariaLabel={label}
                        id={`${id}__${key}`}
                        inputValue={providerFilterData.inputValue}
                        isLoading={providerFilterData.isLoading}
                        label={label}
                        onChange={(provider) =>
                          handleInputChange(key, provider)
                        }
                        onInputChange={providerFilterData.onInputChange}
                        onMenuScrollToBottom={
                          providerFilterData.onMenuScrollToBottom
                        }
                        providerOptions={providerFilterData.providerOptions}
                        value={defaultValue as FilterElement[]}
                      />
                    );
                  case InquiryFilterTypes.SELECT:
                    return (
                      <SelectFilter
                        ariaLabel={label}
                        id={`${id}__${key}`}
                        inputValue=""
                        isSearchable={false}
                        isSingleSelect={isSingleSelect}
                        label={label}
                        onChange={(selectedOption) =>
                          handleInputChange(key, selectedOption)
                        }
                        options={options}
                        placeholder={placeholder}
                        value={defaultValue as FilterElement[]}
                        withCheckBox={!isSingleSelect}
                      />
                    );
                  case InquiryFilterTypes.MIN_MAX:
                    return (
                      <MinMaxFilter
                        id={`${id}__${key}`}
                        label={label}
                        max={max.defaultValue}
                        min={min.defaultValue}
                        setMax={(maxValue) =>
                          handleInputChange(max.key, maxValue)
                        }
                        setMin={(minValue) =>
                          handleInputChange(min.key, minValue)
                        }
                      />
                    );
                  case InquiryFilterTypes.DATE_RANGE:
                    return (
                      <DateRangeFilter
                        endDate={max.defaultValue as string}
                        id={`${id}__${key}`}
                        label={label}
                        minDate={getDefaultInquiryDateMin().toJSDate()}
                        setEndDate={(maxValue) =>
                          handleInputChange(max.key, maxValue)
                        }
                        setStartDate={(minValue) =>
                          handleInputChange(min.key, minValue)
                        }
                        startDate={min.defaultValue as string}
                      />
                    );
                  default:
                    return null;
                }
              })()}
            </FilterWrapper>
          );
        },
      )}
    </ChakraFlex>
  );
};

export default FilterBuilder;
